import { useCallback, useState } from 'react';
import type { AgentSessionDTO } from '@/services/roros/agent-sessions.service';

type ModelTierOverride = 'standard' | 'complex' | null;
type UsageDelta = {
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
};

export function useAgentSessionUsage() {
  // AC-AGS-10: Running token/cost counters (updated from usage chunks or session state)
  const [runningInputTokens, setRunningInputTokens] = useState(0);
  const [runningOutputTokens, setRunningOutputTokens] = useState(0);
  const [runningCostUsd, setRunningCostUsd] = useState(0);

  // AC-AGS-M4-7: model tier override (local state synced with session)
  const [modelTierOverride, setModelTierOverride] = useState<ModelTierOverride>(null);

  const syncCountersFromSession = useCallback((session: AgentSessionDTO) => {
    setRunningInputTokens(session.totalInputTokens ?? 0);
    setRunningOutputTokens(session.totalOutputTokens ?? 0);
    setRunningCostUsd(parseFloat(session.totalCostUsd ?? '0'));
  }, []);

  const syncTierFromSession = useCallback((session: AgentSessionDTO) => {
    setModelTierOverride(session.modelTierOverride ?? null);
  }, []);

  const syncUsageFromSession = useCallback((session: AgentSessionDTO) => {
    syncCountersFromSession(session);
    syncTierFromSession(session);
  }, [syncCountersFromSession, syncTierFromSession]);

  const addUsageFromChunk = useCallback((usage: UsageDelta) => {
    setRunningInputTokens((prev) => prev + (usage.inputTokens ?? 0));
    setRunningOutputTokens((prev) => prev + (usage.outputTokens ?? 0));
    setRunningCostUsd((prev) => prev + (usage.costUsd ?? 0));
  }, []);

  return {
    runningInputTokens,
    runningOutputTokens,
    runningCostUsd,
    modelTierOverride,
    setModelTierOverride,
    syncUsageFromSession,
    syncCountersFromSession,
    syncTierFromSession,
    addUsageFromChunk,
  };
}
